import React, { useState } from 'react';
import { regionsForIso3, ISO3_TO_CC, canonicalRegion } from '../data/nuts';

// Optional province-level delivery refinement, shown under Operating Countries.
// For every selected operating country that HAS sub-regions, the customer can
// expand it and narrow delivery to specific provinces. Leaving a country
// collapsed ("Whole country") keeps today's behaviour. `value` is a flat list
// of NUTS codes (e.g. ["NL11","NL12"]); a country not represented = whole
// country. Countries with no sub-regions (CY, EE, LU, LV, MT) are not shown.

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { fontSize: 13, color: '#94a3b8' },
  country: { border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px' },
  name: { fontSize: 14, fontWeight: 600, color: '#1e293b', flex: 1 },
  toggle: (open) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7,
    fontSize: 13, fontWeight: 600,
    color: open ? '#1d4ed8' : '#475569',
    background: open ? '#eff6ff' : '#f1f5f9',
    border: `1px solid ${open ? '#bfdbfe' : '#e2e8f0'}`,
    padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
  }),
  chev: (open) => ({ fontSize: 9, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }),
  refine: { padding: '4px 14px 16px', background: '#f8fafc', borderTop: '1px solid #f1f5f9' },
  group: { marginTop: 12 },
  groupHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
  groupName: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' },
  groupAll: { fontSize: 11.5, fontWeight: 600, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  chip: (on) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 13, fontWeight: on ? 600 : 500,
    color: on ? '#1d4ed8' : '#475569',
    background: on ? '#eff6ff' : '#fff',
    border: `1px solid ${on ? '#2563eb' : '#cbd5e1'}`,
    padding: '6px 11px', borderRadius: 8, cursor: 'pointer',
  }),
  tick: (on) => ({
    width: 14, height: 14, borderRadius: 4, flexShrink: 0,
    border: `1.5px solid ${on ? '#2563eb' : '#cbd5e1'}`,
    background: on ? '#2563eb' : '#fff',
    color: '#fff', fontSize: 9, lineHeight: '11px', textAlign: 'center',
  }),
  foot: { display: 'flex', justifyContent: 'flex-end', marginTop: 14 },
  reset: { fontSize: 12.5, fontWeight: 600, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  summary: { fontSize: 13, color: '#475569', lineHeight: 1.6 },
  summaryBlue: { color: '#1d4ed8', fontWeight: 600 },
};

export default function RegionSelect({ operatingCountries, value, onChange }) {
  const [open, setOpen] = useState({}); // iso3 -> bool
  // Canonicalise: a profile saved before a NUTS renumbering holds the old code
  // (NL31 for Utrecht), and the chip it belongs to now carries the new one — an
  // uncanonicalised compare would render the customer's own pick as unselected.
  const picks = new Set((value || []).map(canonicalRegion));

  // Only the selected operating countries that actually have sub-regions.
  const blocks = (operatingCountries || [])
    .map((iso3) => ({ iso3, cc: ISO3_TO_CC[iso3], country: regionsForIso3(iso3) }))
    .filter((b) => b.country);

  if (blocks.length === 0) {
    return (
      <div style={s.empty}>
        {(!operatingCountries || operatingCountries.length === 0)
          ? 'Select operating countries above to optionally narrow delivery to specific regions.'
          : 'The selected countries have no sub-regions — delivery is matched country-wide.'}
      </div>
    );
  }

  function codesFor(country) {
    const out = [];
    country.groups.forEach((g) => g.regions.forEach((r) => out.push(r.code)));
    return out;
  }
  function selectedCount(country) {
    return codesFor(country).filter((c) => picks.has(c)).length;
  }

  function apply(next) { onChange(Array.from(next)); }

  function toggleRegion(code) {
    const next = new Set(picks);
    next.has(code) ? next.delete(code) : next.add(code);
    apply(next);
  }
  function toggleGroup(regions) {
    const codes = regions.map((r) => r.code);
    const allOn = codes.every((c) => picks.has(c));
    const next = new Set(picks);
    codes.forEach((c) => (allOn ? next.delete(c) : next.add(c)));
    apply(next);
  }
  function resetCountry(country) {
    const codes = new Set(codesFor(country));
    apply(new Set([...picks].filter((c) => !codes.has(c))));
    setOpen((o) => ({ ...o, [country.iso3]: false }));
  }

  return (
    <div style={s.wrap}>
      {blocks.map(({ iso3, country }) => {
        const isOpen = !!open[iso3];
        const count = selectedCount(country);
        const showGroupHeads = country.groups.length > 1;
        return (
          <div key={iso3} style={s.country}>
            <div style={s.head}>
              <span style={s.name}>{country.name}</span>
              <button
                type="button"
                style={s.toggle(isOpen)}
                onClick={() => setOpen((o) => ({ ...o, [iso3]: !o[iso3] }))}
              >
                {count === 0 ? 'Whole country' : `${count} region${count > 1 ? 's' : ''}`}
                <span style={s.chev(isOpen)}>▼</span>
              </button>
            </div>
            {isOpen && (
              <div style={s.refine}>
                {country.groups.map((g, gi) => (
                  <div key={g.code || gi} style={s.group}>
                    {showGroupHeads && (
                      <div style={s.groupHead}>
                        <span style={s.groupName}>{g.name}</span>
                        <button type="button" style={s.groupAll} onClick={() => toggleGroup(g.regions)}>
                          {g.regions.every((r) => picks.has(r.code)) ? 'Clear' : 'Select all'}
                        </button>
                      </div>
                    )}
                    <div style={s.chips}>
                      {g.regions.map((r) => {
                        const on = picks.has(r.code);
                        return (
                          <button key={r.code} type="button" style={s.chip(on)} onClick={() => toggleRegion(r.code)}>
                            <span style={s.tick(on)}>{on ? '✓' : ''}</span>
                            {r.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {count > 0 && (
                  <div style={s.foot}>
                    <button type="button" style={s.reset} onClick={() => resetCountry(country)}>
                      ↺ Back to whole country
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div style={s.summary}>
        {blocks.every(({ country }) => selectedCount(country) === 0)
          ? 'Matching delivery in the whole of each selected country.'
          : (
            <>Matching in{' '}
              {blocks.map(({ country }, i) => {
                const codes = codesFor(country).filter((c) => picks.has(c));
                const names = country.groups
                  .flatMap((g) => g.regions)
                  .filter((r) => picks.has(r.code))
                  .map((r) => r.name);
                return (
                  <React.Fragment key={country.name}>
                    {i > 0 && ' · '}
                    {codes.length === 0
                      ? <>all of <span style={s.summaryBlue}>{country.name}</span></>
                      : <><span style={s.summaryBlue}>{names.join(', ')}</span> ({country.name})</>}
                  </React.Fragment>
                );
              })}
            </>
          )}
      </div>
    </div>
  );
}
